import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { TbPrinter, TbCheck, TbX } from "react-icons/tb";
import { apiRequest } from "src/services/apiClient";
import { config } from "configs/config";
import "./consentModal.scss";

const CONSENT_TEXT = `Sehr geehrte Patientin, sehr geehrter Patient,
sehr geehrte Eltern,

die Exipnon GmbH, Erdinger Str. 22a, 85646 Anzing wird für ihren Arzt die Abrechnung der ärztlichen/therapeutischen Leistungen vornehmen und die Rechnung erstellen.

Die Praxis steht Ihnen im Rahmen der hierfür notwendigen Datenverarbeitung als datenschutzrechtliche Verantwortliche zur Wahrung Ihrer Rechte und für Rückfragen zur Verfügung. Die Mitarbeiter der Praxis sind Berufsgeheimnisträger, und unterliegen somit der beruflichen Verschwiegenheit sowie den Bestimmungen des Datenschutzes, so wie sie bei Ärztinnen und Ärzten vorgeschrieben sind.

Mit Ihrer Unterschrift willigen Sie in die Datenverarbeitung zwecks Rechnungserstellung ein. Ihre persönlichen Behandlungsdaten bzw. die Behandlungsdaten Ihres Kindes, wie Anschrift, Geburtsdatum, Kostenträger, evtl. Tarife, Behandlungstage, erbrachte Leistungen nach der Gebührenordnung und dazugehörige Diagnosen werden hierzu von der Praxis an die Exipnon GmbH übermittelt. Die Behandlung ist natürlich nicht von dieser Einwilligung abhängig. Sie können auch einzelne Behandlungen von dieser Erklärung ausnehmen. Dann müsste der Leistungserbringer selbst die Abrechnung vornehmen. Nachteile entstehen Ihnen hierdurch nicht.
{{DIGITAL}}
Sie können Ihre Einwilligung mit Wirkung für die Zukunft jederzeit widerrufen. Bis zum Zeitpunkt des Widerrufs erfolgte Datenverarbeitungen bleiben rechtmäßig. Ihre Daten dürfen dann noch insoweit weiter verarbeitet werden, wie dies aufgrund gesetzlicher Vorschriften erforderlich ist. Der Widerruf kann gegenüber dem Leistungserbringer (Praxis) oder der Exipnon mittels schriftlicher Erklärung unter Angabe Ihres Namens und Anschrift (ggf. Rechnungsnummer) geschehen. Auch hierdurch entstehen Ihnen keine Nachteile.

Gleichzeitig informieren wir Sie darüber, dass der Leistungserbringer die Honorarforderung zum Forderungseinzug an die Fa. Paywise abtreten kann. Die Entscheidung darüber obliegt alleine dem Leistungserbringer und wird unter Wahrung der Datenschutzrechtlichen Anforderungen erfolgen.

Weitergehende Informationen zum Thema Datenschutz bei der Exipnon GmbH und zu Ihren Rechten erfahren Sie unter: www.exipnon.eu/datenschutz

Hiermit willige ich in die Datenverarbeitung durch die Exipnon GmbH zu den vorgenannten Zwecken ein und entbinde insoweit den Leistungserbringer und dessen berufsmäßige Gehilfen zugleich von der ärztlichen Schweigepflicht.`;

/** Digital consent only — same sentence as DIGITAL_SENDING_SENTENCE in backend generateConsentPdf.ts. */
const DIGITAL_SENDING_SENTENCE = "Mit Ihrer Unterschrift willigen Sie in den digitalen Versand Ihrer Rechnung ein.";

const consentText = (channel: "digital" | "post") =>
    CONSENT_TEXT.replace("{{DIGITAL}}\n", channel === "digital" ? `\n${DIGITAL_SENDING_SENTENCE}\n\n` : "\n");

/** Same line as COVERED_PATIENTS_LABEL in backend generateConsentPdf.ts. */
const COVERED_PATIENTS_LABEL = "Diese Einwilligung gilt für:";

interface Props {
    recipientId:   string;
    recipientName: string;
    /** Every patient of the recipient — saving covers exactly these (each patient needs their
     *  own consent; backend saveConsent records them). */
    patientNames?: string[];
    /** Which dropdown this manual confirmation was triggered from — drives the
     *  "(manuell gesetzt)" header badge shown afterward. */
    channel:       "digital" | "post";
    onClose:       () => void;
    onSaved:       () => void;
}

export function ConsentModal({ recipientId, recipientName, patientNames, channel, onClose, onSaved }: Props) {
    const place    = JSON.parse(localStorage.getItem("praxis") || "null")?.city ?? "";
    const signedBy = recipientName;
    const today    = new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

    const [physConfirm, setPhysConfirm] = useState(false);
    const [saving,      setSaving]      = useState(false);
    const [error,       setError]       = useState<string | null>(null);

    const openPrintPdf = useCallback(async () => {
        try {
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const res = await fetch(`${config.baseUrl}/api/recipients/${recipientId}/consent-pdf/preview`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
                body: JSON.stringify({ signedBy, signedPlace: place, channel }),
            });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            window.open(URL.createObjectURL(blob), "_blank");
        } catch {
            setError("PDF konnte nicht geöffnet werden.");
        }
    }, [recipientId, signedBy, place, channel]);

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await apiRequest(`/recipients/${recipientId}/consent`, {
                method: "POST",
                body: JSON.stringify({ signedBy, signedPlace: place, signatureType: "physical", channel }),
            }, { "Content-Type": "application/json" });
            onSaved();
            onClose();
        } catch (err: any) {
            setError(err?.message ?? "Fehler beim Speichern.");
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="consent-overlay" onClick={onClose}>
            <div className="consent-modal" onClick={(e) => e.stopPropagation()}>
                <div className="consent-modal__header">
                    <span>Datenschutzerklärung & Einwilligung</span>
                    <button className="consent-modal__close" onClick={onClose}><TbX /></button>
                </div>

                <div className="consent-modal__body">
                    {!!patientNames?.length && (
                        <p className="consent-covered">{COVERED_PATIENTS_LABEL} {patientNames.join(", ")}</p>
                    )}
                    <div className="consent-text">{consentText(channel)}</div>

                    <div className="consent-fields">
                        <div className="consent-field">
                            <label>Ort</label>
                            <input type="text" value={place} readOnly disabled />
                        </div>
                        <div className="consent-field consent-field--date">
                            <label>Datum</label>
                            <input type="text" value={today} readOnly disabled />
                        </div>
                        <div className="consent-field consent-field--name">
                            <label>Name (Unterzeichner)</label>
                            <input type="text" value={signedBy} readOnly disabled />
                        </div>
                    </div>

                    <div className="consent-physical">
                        <button className="consent-print-btn" onClick={openPrintPdf}>
                            <TbPrinter /> Dokument öffnen / drucken
                        </button>
                        <label className="consent-confirm-check">
                            <input
                                type="checkbox"
                                checked={physConfirm}
                                onChange={(e) => setPhysConfirm(e.target.checked)}
                            />
                            <span>Unterschrift liegt vor und wurde vom Patienten eigenhändig geleistet</span>
                        </label>
                    </div>

                    {error && <p className="consent-error">{error}</p>}
                </div>

                <div className="consent-modal__footer">
                    <button
                        className="consent-modal__save"
                        disabled={saving || !physConfirm}
                        onClick={save}
                    >
                        <TbCheck /> {saving ? "Wird gespeichert…" : "Einwilligung speichern"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
